// `GET /v1/me/auth/get-session` é a **única** fonte de identidade dos guards
// (`spec/17-frontend.md` §4.3): é a única rota que não passa por `auth-session` e
// portanto a única que responde nos estados que o guard precisa distinguir
// (`terms_not_accepted`, `password_change_required`, `mfa_required`).
//
// O corpo vem do Better Auth em camelCase, com os `additionalFields` do projeto
// (`packages/auth/src/better-auth.ts`) em snake_case: `role`, `seller_id`,
// `must_change_password`. `twoFactorEnabled` vem do plugin `twoFactor`.

import { queryOptions } from '@tanstack/react-query';
import { api } from './client.ts';

export interface SessionUser {
  id: string;
  email: string;
  name: string;
  emailVerified: boolean;
  role: string;
  sellerId: string | null;
  twoFactorEnabled: boolean;
  mustChangePassword: boolean;
}

export const SESSION_QUERY_KEY = ['session'] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function normalize(raw: unknown): SessionUser | null {
  if (!isRecord(raw) || !isRecord(raw.user)) {
    return null;
  }
  const user = raw.user;
  return {
    id: typeof user.id === 'string' ? user.id : '',
    email: typeof user.email === 'string' ? user.email : '',
    name: typeof user.name === 'string' ? user.name : '',
    emailVerified: user.emailVerified === true,
    role: typeof user.role === 'string' ? user.role : 'seller_owner',
    sellerId: typeof user.seller_id === 'string' ? user.seller_id : null,
    twoFactorEnabled: user.twoFactorEnabled === true,
    mustChangePassword: user.must_change_password === true,
  };
}

export async function getSession(): Promise<SessionUser | null> {
  return normalize(await api<unknown>('/v1/me/auth/get-session'));
}

export const sessionQueryOptions = queryOptions({
  queryKey: SESSION_QUERY_KEY,
  queryFn: getSession,
  staleTime: 30_000,
  retry: false,
});
